import React, { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import { Modal, Container, Form } from "react-bootstrap";
import {
	FormProvider,
	useFieldArray,
	useForm,
	useWatch,
} from "react-hook-form";

import {
	CloseButton,
	EmptyBlock,
	Button,
	Skeleton,
	HookFormCheckbox,
} from "components";
import useDocumentsService from "components/tables/documents/useDocumentsService";
import { applicationDocumentsService } from "services";
import { DocumentTypes } from "types";

import styles from "./appListDocuments.module.scss";

const DOCUMENTS_ARRAY_NAME = "documents";

const AppDocumentsModal = ({
	show,
	onHide,
	backdrop,
	dialogAs,
	applicationId,
	isPaperApplication,
	saveDocumentsCopy,
}) => {
	const [isSelectAllActive, setIsSelectAllActive] = useState(false);
	const [isAddBtnDisabled, setIsAddBtnDisabled] = useState(true);

	const formMethods = useForm();
	const { control, register, reset, setValue, getValues, handleSubmit } =
		formMethods;

	const { fields } = useFieldArray({ control, name: DOCUMENTS_ARRAY_NAME });

	const watch = useWatch({
		control,
	});

	useEffect(() => {
		const documentChecked =
			watch?.[DOCUMENTS_ARRAY_NAME]?.findIndex(
				document => document.checkbox,
			) > -1;

		setIsAddBtnDisabled(!documentChecked);
	}, [watch]);

	const { useDocumentsListQuery } = useDocumentsService({
		key: ["copyApplication", applicationId],
		service: applicationDocumentsService,
	});

	const { data, isFetching } = useDocumentsListQuery({
		entityId: applicationId,
		page: 0,
	});

	const latestMpaDocumentId = useMemo(() => {
		let latestMpaDocument = { id: "", created_at: 0 };

		data?.items?.forEach(document => {
			if (
				document.type === DocumentTypes.ApplicationUnredacted.value &&
				document.deleted_at === null
			) {
				if (
					moment(latestMpaDocument.created_at).isBefore(
						document.created_at,
					)
				) {
					latestMpaDocument = document;
				}
			}
		});

		return latestMpaDocument.id;
	}, [data?.items]);

	useEffect(() => {
		const fields = data?.items?.map(document => ({
			checkbox: false,
			document_name: document.name,
			document_id: document.id,
			created_at: document.created_at,
		}));

		reset({
			documents: fields,
		});
	}, [data?.items, reset]);

	const handleSelectAllClick = useCallback(() => {
		if (Array.isArray(getValues()[DOCUMENTS_ARRAY_NAME])) {
			getValues().documents.forEach((item, index) => {
				setValue(
					`${DOCUMENTS_ARRAY_NAME}.${index}.checkbox`,
					isSelectAllActive ? false : true,
				);
			});
		}

		setIsSelectAllActive(!isSelectAllActive);
	}, [getValues, isSelectAllActive, setValue]);

	const closeModal = () => {
		onHide();
	};

	const handleSkip = () => {
		saveDocumentsCopy([], data?.items);
		closeModal();
	};

	const handleAddDocuments = formData => {
		const documents = formData[DOCUMENTS_ARRAY_NAME];
		if (Array.isArray(documents)) {
			const selectedDocuments = documents.filter(
				document => document.checkbox,
			);

			let sortedDocuments = selectedDocuments.sort(function compareFn(
				documentA,
				documentB,
			) {
				if (documentA.created_at === documentB.created_at) {
					return 0;
				}
				if (moment(documentA.created_at).isBefore(documentB.created_at))
					return -1;
				return 1;
			});

			saveDocumentsCopy(
				sortedDocuments?.map(document => document.document_id),
				data?.items,
			);

			closeModal();
		}
	};

	const Placeholder = useCallback(() => {
		return isFetching ? <Skeleton /> : <EmptyBlock className="d-flex" />;
	}, [isFetching]);

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			scrollable
			animation={false}
			backdrop={backdrop}
			dialogAs={dialogAs}
			size="xl"
		>
			<Modal.Header>
				<Modal.Title>Select Attachments to Copy</Modal.Title>
				<CloseButton onClick={onHide} />
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<Container className={styles.container}>
					{data?.items?.length ? (
						<FormProvider {...formMethods}>
							<Form>
								<Button
									onClick={handleSelectAllClick}
									variant="secondary"
									minWidth="100px"
									value={
										isSelectAllActive
											? "Deselect All"
											: "Select All"
									}
									className={styles.selectAllBtn}
								/>
								<hr className={styles.divider} />
								{fields.map((field, index) => {
									const isMpaDocument =
										field.document_id ===
											latestMpaDocumentId &&
										isPaperApplication;

									return (
										<div
											key={field.id}
											className={styles.documentItem}
										>
											<HookFormCheckbox
												register={register}
												name={`${DOCUMENTS_ARRAY_NAME}.${index}.checkbox`}
												id={`${DOCUMENTS_ARRAY_NAME}.${index}.checkbox`}
												label={field.document_name}
											/>
											{isMpaDocument && (
												<div
													className={
														styles.mpaDocumentLabel
													}
												>
													MPA Document
												</div>
											)}
											<input
												{...register(
													`${DOCUMENTS_ARRAY_NAME}.${index}.document_id`,
												)}
												defaultValue={field.document_id}
												hidden
											/>
											<input
												{...register(
													`${DOCUMENTS_ARRAY_NAME}.${index}.created_at`,
												)}
												defaultValue={field.created_at}
												hidden
											/>
										</div>
									);
								})}
							</Form>
						</FormProvider>
					) : (
						<Placeholder />
					)}
				</Container>
			</Modal.Body>
			<Modal.Footer className="d-flex justify-content-center">
				<Button
					onClick={handleSkip}
					value="Skip"
					variant="outline-primary"
					className="mr-4"
				/>
				<Button
					onClick={handleSubmit(handleAddDocuments)}
					value="Add"
					variant="primary"
					disabled={isAddBtnDisabled}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default AppDocumentsModal;
